import React, { useRef } from "react";
import { Box, IconButton, TextField, Typography } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { DateTime } from "luxon";

type DayNavProps = {
  /** 選択中の日付 (Luxon DateTime) */
  selectedDate: DateTime;
  /** 日付変更コールバック */
  onChange: (date: DateTime) => void;
  /** 未来日への移動を無効化（デフォルト: false = 未来日も選択可） */
  disableFuture?: boolean;
};

const WEEKDAYS = ["月", "火", "水", "木", "金", "土", "日"];

/**
 * 日付ナビゲーション共通コンポーネント。
 *
 * [◀] 2026年4月14日(月) [▶]
 *
 * - ◀ ▶ で 1 日ずつ前後移動
 * - 日付表示をクリックするとネイティブ日付ピッカーが開き、
 *   直接任意の日付にジャンプ可能（30日先の予約入力時など）
 */
const DayNav: React.FC<DayNavProps> = ({
  selectedDate,
  onChange,
  disableFuture = false,
}) => {
  const hiddenDateInputRef = useRef<HTMLInputElement | null>(null);

  const handlePrev = () => {
    onChange(selectedDate.minus({ days: 1 }));
  };

  const handleNext = () => {
    onChange(selectedDate.plus({ days: 1 }));
  };

  const openDatePicker = () => {
    const el = hiddenDateInputRef.current;
    if (!el) return;
    // 新しいブラウザで showPicker() が使える場合はそれで、
    // 未対応ブラウザは focus + click でネイティブ UI を開く
    if (typeof (el as any).showPicker === "function") {
      try {
        (el as any).showPicker();
        return;
      } catch {
        // fallthrough
      }
    }
    el.focus();
    el.click();
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value; // yyyy-MM-dd
    if (!v) return;
    const dt = DateTime.fromISO(v);
    if (dt.isValid) onChange(dt);
  };

  const isToday =
    selectedDate.toFormat("yyyy-MM-dd") ===
    DateTime.local().toFormat("yyyy-MM-dd");
  const isFutureDisabled = disableFuture && isToday;

  const weekdayIndex = selectedDate.weekday - 1; // 1=月 → 0
  const weekdayLabel = WEEKDAYS[weekdayIndex] || "";
  const isWeekend = selectedDate.weekday >= 6;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      style={{
        backgroundColor: "#f5f5f5",
        padding: "2px 4px",
        border: "1px solid #e0e0e0",
        borderRadius: 4,
        position: "relative",
      }}
    >
      <IconButton onClick={handlePrev} size="small">
        <ChevronLeftIcon />
      </IconButton>
      <Typography
        onClick={openDatePicker}
        title="クリックして日付を選択"
        style={{
          fontWeight: "bold",
          fontSize: 15,
          minWidth: 160,
          textAlign: "center",
          color: isWeekend ? "#e53935" : undefined,
          cursor: "pointer",
          userSelect: "none",
          padding: "0 8px",
          borderBottom: "1px dashed transparent",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.borderBottomColor = "#999")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.borderBottomColor = "transparent")
        }
      >
        {selectedDate.toFormat("yyyy年M月d日")}({weekdayLabel})
      </Typography>
      {/* ネイティブ日付ピッカーを開くための隠し input */}
      <TextField
        type="date"
        inputRef={hiddenDateInputRef}
        value={selectedDate.toFormat("yyyy-MM-dd")}
        onChange={handleDateInputChange}
        InputProps={{
          disableUnderline: true,
          inputProps: disableFuture
            ? { max: DateTime.local().toFormat("yyyy-MM-dd") }
            : undefined,
        }}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: 0,
          height: 0,
        }}
      />
      <IconButton
        onClick={handleNext}
        size="small"
        disabled={isFutureDisabled}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default DayNav;
